import { NodeTypeDeterminer } from './node-type';
import { setCursorOnTag, setTextCursor } from './cursor-utils';

export class CursorMoveVertically {

  private readonly nodeManager: NodeTypeDeterminer;
  private readonly parentElement: () => Element;

  constructor(nodeManager: NodeTypeDeterminer, parentElement: () => Element) {
    this.nodeManager = nodeManager;
    this.parentElement = parentElement;
  }

  moveCursorVertically(cursorRect: ClientRect, cursorNode: Node, toUp: boolean): boolean {
    let self = this;
    const rows = new Rows();

    this.nodeManager.forEachChild(this.parentElement(), {
      visitCell(id, editor) {
        rows.add(editor.element);
      },
      visitDelimiter(dl) {
        rows.add(dl);
      },
      visitLineBreak(lb) {
        rows.add(lb);
        rows.newRow();
      },
      visitServiceBreak(sb) {
        rows.add(sb);
      },
      visitText(t) {
        rows.add(t);
      }
    });

    const cursorRowNumber = rows.getRowNumberByNode(cursorNode);
    if (cursorRowNumber !== undefined) {
      const newRowNumber = toUp ? (cursorRowNumber - 1) : (cursorRowNumber + 1);
      if (newRowNumber >= 0 && newRowNumber < rows.rows.length) {
        const newRow = rows.rows[newRowNumber];

        const target = newRow.getNodeByX(cursorRect.left);
        if (target) {
          return this.nodeManager.determineType(target.node, {
            visitCell(id, editor) {
              const selection = document.getSelection();
              if (selection) {
                selection.removeAllRanges();
              }
              editor.setCursorByXY(cursorRect.left, toUp ? cursorRect.top : cursorRect.bottom);
              return true;
            },
            visitDelimiter(dl) {
              setCursorOnTag(dl);
              return true;
            },
            visitLineBreak(lb) {
              setCursorOnTag(lb);
              return true;
            },
            visitServiceBreak(sb) {
              return false;
            },
            visitText(t) {
              
                let offset;
                const x = cursorRect.left;
                if (x < target.rect.left) {
                  offset = 0;
                } else if (x > target.rect.right) {
                  if (t.textContent) {
                    offset = t.textContent.length;
                  }
                  else {
                    offset = 0;
                  }
                } else {
                  if (t.textContent) {
                    const distTillCursor = x - target.rect.left;
                    const ratio = distTillCursor / target.rect.width;
                    offset = Math.round(t.textContent.length * ratio);
                   
                  }
                  else {
                    offset = 0;
                  }
                }

                setTextCursor(t, offset);
                return true;
             
            }
          });
        }
      }
    }

    return false;
  }

  //returns true if set to cell
  moveCurrentTextCursorVertically(toUp: boolean): boolean {

    const cursor = this.getCursor();

    if (cursor) {
      return this.moveCursorVertically(cursor.rect, cursor.node, toUp);
    }
    return false;

  }

  private getCursor(): { node: Node, rect: ClientRect } | undefined {
    const sel = document.getSelection();

    if (sel && sel.type === "Caret") {

      const an = sel.anchorNode;
      if (an) {
        const offset = sel.anchorOffset;
        if (an.nodeType === Node.TEXT_NODE) {
          //cursor within the text at index===offset
          const rect = sel.getRangeAt(0).getBoundingClientRect();
          return { node: an, rect };
        }
        else {
          if (offset < an.childNodes.length) {
            //cursor before the child
            const child = an.childNodes.item(offset);
            const el = child as Element;
            if (el) {
              const tagRect = el.getBoundingClientRect();
              const cursorRect = { right: tagRect.left, left: tagRect.left, top: tagRect.top, bottom: tagRect.bottom, width: 0, height: tagRect.height };
              return { node: child, rect: cursorRect };
            }
            else {
              return { node: child, rect: sel.getRangeAt(0).getBoundingClientRect() };
            }

          }
          else {
            //cursor after the child
            const child = an.childNodes.item(an.childNodes.length - 1);
            const el = child as Element;
            if (el) {
              const tagRect = el.getBoundingClientRect();

              const cursorRect = { right: tagRect.right, left: tagRect.right, top: tagRect.top, bottom: tagRect.bottom, width: 0, height: tagRect.height };
              return { node: child, rect: cursorRect };
            }
            else {
              return { node: child, rect: sel.getRangeAt(0).getBoundingClientRect() };
            }
          }
        }
      }
    }
  }
}

class Row {
  readonly elements: Node[] = [];
  readonly rects: ClientRect[] = [];

  add(n: Node) {
    this.elements.push(n);
    const r = document.createRange();
    r.selectNode(n);
    this.rects.push(r.getBoundingClientRect());
    r.detach();
  }

  getNodeByX(x: number): { node: Node, rect: ClientRect } | undefined {

    if (this.elements.length > 0) {

      if (this.rects[0].left >= x) {
        return { node: this.elements[0], rect: this.rects[0] };
      }

      if (this.rects[this.rects.length - 1].right <= x) {
        return { node: this.elements[this.elements.length - 1], rect: this.rects[this.rects.length - 1] };
      }

      for (let i = 0; i < this.elements.length; i++) {
        const r = this.rects[i];
        if (x <= r.right) {
          return { node: this.elements[i], rect: this.rects[i] };
        }
      }
    }
  }
}

class Rows {
  readonly rows: Row[] = [];

  constructor() {
    this.newRow();
  }

  newRow() {
    this.rows.push(new Row());
  }

  get lastRow(): Row {
    return this.rows[this.rows.length - 1];
  }

  getRowNumberByNode(node: Node): number | undefined {
    for (let i = 0; i < this.rows.length; i++) {
      const r = this.rows[i];
      if (r.elements.indexOf(node) >= 0) {
        return i;
      }
    }
  }

  add(n: Node) {
    this.lastRow.add(n);
  }
}